declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

export interface FlowAnalyticsEvent {
  action: 'start' | 'step_view' | 'step_complete' | 'finish' | 'exit';
  flowId: number;
  stepId?: number;
  duration?: number;
  platform?: string;
}

const detectPlatform = (): string => {
  if (typeof navigator === 'undefined') return 'Web';

  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone/i.test(userAgent)) return 'Mobile';
  if (/android/i.test(userAgent) && /mobile/i.test(userAgent)) return 'Mobile';
  if (/ipad/i.test(userAgent)) return 'Mobile'; // iPad is considered mobile for analytics
  if (/android/i.test(userAgent) && !/mobile/i.test(userAgent)) return 'Mobile'; // Android tablets
  if (/webos|blackberry|iemobile|opera mini/i.test(userAgent)) return 'Mobile';

  return 'Web';
};

const generateSessionId = (): string => {
  return `flow_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getOrCreateSessionId = (): string => {
  const sessionKey = 'flowAnalyticsSessionId';
  let sessionId = sessionStorage.getItem(sessionKey);

  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(sessionKey, sessionId);
  }

  return sessionId;
};

export const clearFlowAnalyticsSession = (): void => {
  try {
    sessionStorage.removeItem('flowAnalyticsSessionId');

    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      if (key.startsWith('flow_start_')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Error clearing flow analytics session:', error);
  }
};

const sendToGA4 = (eventName: string, parameters: Record<string, any>) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      const enhancedParameters = {
        page_path: window.location.pathname,
        page_location: window.location.href,
        page_title: document.title,
        ...parameters,
      };

      window.gtag('event', eventName, enhancedParameters);
    }
  } catch (error) {
    console.error('❌ Error sending flow analytics to GA4:', error);
  }
};

const sendToDatabase = async (eventData: any) => {
  try {
    const { default: PublicAPI } = await import('../api/TreedisPublicDAO');
    const response = await PublicAPI.logFlowsAnalytics({
      data: eventData,
    });
    return response;
  } catch (error) {
    console.error('❌ Error saving flow analytics to database:', error);
    throw error;
  }
};

export const sendFlowAnalyticsEvent = async (event: FlowAnalyticsEvent) => {
  try {
    const eventName = `flow_${event.action}`;

    const getUserData = () => {
      try {
        // Only collect data from Introscreen lead form
        const leadFormData = localStorage.getItem('leadFormData');
        if (leadFormData) {
          try {
            const formData = JSON.parse(leadFormData);
            // If lead form is enabled and has data, use it
            if (formData.name || formData.email) {
              return {
                user_email: formData.email || 'anonymous',
                user_name: formData.name || 'anonymous',
              };
            }
          } catch (error) {
            console.warn('Error parsing lead form data:', error);
          }
        }

        // If no lead form data or lead form is disabled, show as anonymous
        return {
          user_email: 'anonymous',
          user_name: 'anonymous',
        };
      } catch (error) {
        console.warn('Error parsing user data for analytics:', error);
        return {
          user_email: 'anonymous',
          user_name: 'anonymous',
        };
      }
    };

    const userData = getUserData();
    const platform = detectPlatform();
    const sessionId = getOrCreateSessionId();

    if (event.action === 'start') {
      const duplicateKey = `flow_start_${event.flowId}_${sessionId}`;
      const hasStarted = sessionStorage.getItem(duplicateKey);

      if (hasStarted) {
        return;
      }

      sessionStorage.setItem(duplicateKey, 'true');
    }

    const databaseData = {
      flowId: event.flowId,
      action: event.action,
      platform: platform,
      sessionId: sessionId,
      ...(event.stepId && { stepId: event.stepId }),
      ...(userData.user_email && { userEmail: userData.user_email }),
      ...(userData.user_name && { userName: userData.user_name }),
      ...(event.duration && { duration: event.duration }),
      ...(event.platform && { platform: event.platform }),
    };

    const ga4Parameters = {
      event_category: 'flow_interaction',
      event_label: `${event.action}_${event.flowId}`,
      flow_action: event.action,
      platform: platform,
    };

    await Promise.all([sendToDatabase(databaseData), sendToGA4(eventName, ga4Parameters)]);
  } catch (error) {
    console.error('💥 Critical error in flow analytics event:', error);
  }
};

export const trackFlowStart = (flowId: number) => {
  return sendFlowAnalyticsEvent({
    action: 'start',
    flowId,
  });
};

export const trackFlowStepView = (flowId: number, stepId: number) => {
  return sendFlowAnalyticsEvent({
    action: 'step_view',
    flowId,
    stepId,
  });
};

export const trackFlowStepComplete = (flowId: number, stepId: number, duration?: number) => {
  return sendFlowAnalyticsEvent({
    action: 'step_complete',
    flowId,
    stepId,
    duration,
  });
};

export const trackFlowFinish = (flowId: number, duration?: number) => {
  return sendFlowAnalyticsEvent({
    action: 'finish',
    flowId,
    duration,
  });
};

export const trackFlowExit = (flowId: number, stepId?: number, duration?: number) => {
  return sendFlowAnalyticsEvent({
    action: 'exit',
    flowId,
    stepId,
    duration,
  });
};
